// Shared "gamma importance" helpers used by KeyLevelsStrip to rank strikes.
//
// Importance at a strike = total gamma concentration = |call Γ net| + |put Γ net|.
// This is the PROTOTYPE heuristic over the live flow chain (cumulativeGammaNet).
// It is a PLACEHOLDER for the planned /api/gex-levels endpoint (real 0-GEX +
// dealer gamma walls from greek_exposures). Known limitation: for 0DTE late in
// the session, gamma concentrates ATM, so the very top picks hug spot — which is
// exactly why "important strikes" are usually ranked from OUTSIDE the ATM block.

export function strikeGammaMag(bucket) {
  if (!bucket) return 0;
  const c = Math.abs(Number(bucket.call?.cumulativeGammaNet) || 0);
  const p = Math.abs(Number(bucket.put?.cumulativeGammaNet) || 0);
  return c + p;
}

export function strikeNetGamma(bucket) {
  if (!bucket) return 0;
  return (Number(bucket.call?.cumulativeGammaNet) || 0) +
         (Number(bucket.put?.cumulativeGammaNet)  || 0);
}

// Nearest strike to a price (chain.strikes is ascending).
export function nearestStrike(strikes, price) {
  if (price == null || !Array.isArray(strikes) || strikes.length === 0) return null;
  let best = strikes[0], bd = Math.abs(strikes[0] - price);
  for (let i = 1; i < strikes.length; i++) {
    const d = Math.abs(strikes[i] - price);
    if (d < bd) { best = strikes[i]; bd = d; }
  }
  return best;
}

// Top-N strikes by gamma importance on one side of spot.
//   side: 'above' | 'below'
//   excludeSet: optional Set<number> of strikes to skip (e.g. the ATM block)
// Returns [{ strike, mag, net }] sorted by STRIKE ascending.
export function topGammaStrikes(chain, spot, side, count = 3, excludeSet = null) {
  if (spot == null || !chain?.strikes?.length) return [];
  const scored = chain.strikes
    .filter((k) => (excludeSet ? !excludeSet.has(k) : true) && (side === 'above' ? k > spot : k < spot))
    .map((k) => {
      const b = chain.map.get(k);
      return { strike: k, mag: strikeGammaMag(b), net: strikeNetGamma(b) };
    })
    .filter((s) => s.mag > 0)
    .sort((a, b) => b.mag - a.mag)
    .slice(0, count)
    .sort((a, b) => a.strike - b.strike);
  return scored;
}

// Approximate 0-GEX: strike where cumulative net gamma (scanning low -> high)
// crosses zero. PLACEHOLDER for the real /api/gex-levels endpoint.
export function gammaFlipStrike(chain) {
  if (!chain?.strikes?.length) return null;
  let running = 0, prev = null;
  for (const k of chain.strikes) {
    const next = running + strikeNetGamma(chain.map.get(k));
    if (prev != null && prev !== 0 && Math.sign(next) !== Math.sign(prev)) return k;
    running = next; prev = next;
  }
  return null;
}

// Compact gamma magnitude for labels (e.g. 12,340 -> "12.3k").
export function fmtMag(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}
